import dayjs from 'dayjs';

import locale from './locale';

// TODO: reivew formatting date and time functions and remove
//       unnecessary ones

export function formatDateTime(date, time, format) {
  let d = dayjs.isDayjs(date) ? date : dayjs(date);
  if (!d.isValid()) {
    throw new Error('Invalid date input');
  }
  if (time) {
    const { h, m, s } = parseTimeToHMS(time);
    d = d.hour(h).minute(m).second(s).millisecond(0);
  }
  if (format) {
    return d.format(format);
  }
  return time ? d.format() : d.format('YYYY-MM-DD');
}

/**
 * Accepts: Dayjs instance, JS Date, or date-like string.
 * Returns: UTC date string 'YYYY-MM-DD'
 *
 * Parsing rules:
 * - Dayjs: used directly
 * - Date: converted via dayjs(date)
 */
export function toUtcDate(date) {
  const d = dayjs.isDayjs(date) ? date : dayjs(date);
  if (!d.isValid()) {
    throw new Error('Invalid date input');
  }
  return d.format('YYYY-MM-DD');
}

/**
 * Accepts: Dayjs instance, JS Date, or date-like string.
 * Returns: time string 'HH:mm'
 *
 * Parsing rules:
 * - Dayjs: used directly
 * - Date: converted via dayjs(date)
 */
export function toTime(date) {
  const d = dayjs.isDayjs(date) ? date : dayjs(date);
  if (!d.isValid()) {
    throw new Error('Invalid date input');
  }
  return d.format('HH:mm');
}

export function toUtcDateTime(date) {
  const d = dayjs.isDayjs(date) ? date : dayjs(date);
  if (!d.isValid()) {
    throw new Error('Invalid date input');
  }
  return d.format();
}

function parseTimeToHMS(time) {
  const parts = time.split(':').map((p) => Number(p));
  if (parts.length < 2 || parts.some(Number.isNaN)) {
    throw new Error("Invalid time. Use 'HH:mm' or 'HH:mm:ss'.");
  }
  const [h, m, s = 0] = parts;
  if (h < 0 || h > 23 || m < 0 || m > 59 || s < 0 || s > 59) {
    throw new Error('Time out of range.');
  }
  return { h, m, s };
}

export function formatDateRange(from, to, options = { dateStyle: 'medium' }) {
  const fromDate = new Date(from);
  const toDate = new Date(to);
  const fmt = new Intl.DateTimeFormat(locale, options);

  if (fromDate.toDateString() === toDate.toDateString()) {
    return fmt.format(fromDate);
  }

  if (typeof fmt.formatRange === 'function') {
    return fmt.formatRange(fromDate, toDate);
  }

  return `${fmt.format(fromDate)} – ${fmt.format(toDate)}`;
}

export function formatDate(date, options = { dateStyle: 'medium' }) {
  return new Intl.DateTimeFormat(locale, options).format(new Date(date));
}

/**
 * Generate an inclusive range of Dayjs dates (date-only, no time) between start and end.
 *
 * Accepts either date strings or Dayjs instances. Both inputs are normalized to
 * date-only via .startOf('day'), and the returned Dayjs objects are also date-only.
 *
 * Notes:
 *     - Strings should be reliably parseable by Dayjs (e.g., '2026-01-15').
 *     - If start > end after parsing, returns an empty array.
 *     - All returned items are Dayjs instances at startOf('day').
 *
 * @param {string | dayjs.Dayjs} startDate
 *     The start date (inclusive), as a string or Dayjs instance.
 * @param {string | dayjs.Dayjs} endDate
 *     The end date (inclusive), as a string or Dayjs instance.
 * @returns {dayjs.Dayjs[]}
 *     An array of Dayjs objects for each calendar day from start to end, inclusive.
 */
export function daysRange(startDate, endDate) {
  const start = dayjs.isDayjs(startDate)
    ? startDate.startOf('day')
    : dayjs(startDate).startOf('day');
  const end = dayjs.isDayjs(endDate)
    ? endDate.startOf('day')
    : dayjs(endDate).startOf('day');

  if (!start.isValid() || !end.isValid()) {
    throw new Error(
      'Invalid date input: startDate or endDate is not a valid date',
    );
  }

  if (start.isAfter(end, 'day')) {
    return [];
  }

  const days = [];
  let cur = start;

  while (cur.isSame(end, 'day') || cur.isBefore(end, 'day')) {
    days.push(cur);
    cur = cur.add(1, 'day').startOf('day'); // ensure date-only
  }

  return days;
}

export function isoWeekday(dateStr) {
  const d = dayjs(dateStr);
  const w = d.day();
  return w === 0 ? 7 : w;
}
